import { useState, forwardRef } from "react";
import { Spinner } from "reactstrap";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { useProvinces } from "../../Hooks/use-locations";

/**
 * Panel de agregar ciudades.
 *
 * Antes era un modal. Es un panel fijo a la derecha porque la tarea es
 * iterativa —eliges departamento, marcas ciudades, guardas, sigues con el
 * siguiente— y el modal tapaba justo la tabla contra la que se comprueba lo
 * que se acaba de guardar.
 *
 * Todas las ciudades marcadas comparten el mismo costo: es un único PATCH a
 * /logistics/cities con la lista de ids.
 */
export const AddCitiesPanel = forwardRef(({ countryId, onSave, currency }, ref) => {
  const [provinceId, setProvinceId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [shippingValue, setShippingValue] = useState("");

  const { getData } = useDataFetch();
  const { provinces } = useProvinces(countryId);

  const { data: citiesData, isLoading: loadingCities } = getData(
    ["cities-without-shipping", provinceId],
    `/logistics/cities-without-shipping/${provinceId}`,
    { enabled: !!provinceId }
  );

  const cities = citiesData?.data || [];

  const toggleCity = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );

  const handleProvince = (event) => {
    setProvinceId(event.target.value);
    // Las marcas son de la lista anterior: dejarlas puestas guardaría ciudades
    // de un departamento que ya no está a la vista.
    setSelectedIds([]);
  };

  const handleSave = async () => {
    if (!selectedIds.length) {
      toast.error("Selecciona al menos una ciudad");
      return;
    }
    if (!shippingValue) {
      toast.error("Ingresa un costo de envío");
      return;
    }

    try {
      await onSave(selectedIds, Number(shippingValue));
      setSelectedIds([]);
      setShippingValue("");
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar las ciudades");
    }
  };

  return (
    <div className="ui-card" ref={ref}>

      <h3 className="ui-card__title">Agregar ciudades</h3>
      <p className="ui-hint" style={{ marginBottom: 16 }}>
        Elige un departamento y marca las ciudades. Todas las marcadas
        comparten el mismo costo.
      </p>

      <label className="ui-label" htmlFor="province">Departamento</label>
      <select
        id="province"
        className="ui-input w-100"
        value={provinceId}
        onChange={handleProvince}
      >
        <option value="">Selecciona un departamento</option>
        {provinces.map((province) => (
          <option key={province.id} value={province.id}>
            {province.name}
          </option>
        ))}
      </select>

      <label className="ui-label" style={{ margin: "14px 0 6px" }}>
        Ciudades sin costo
      </label>

      {!provinceId ? (
        <div className="ui-empty">Elige un departamento primero.</div>
      ) : loadingCities ? (
        <div className="text-center py-3"><Spinner size="sm" /></div>
      ) : cities.length === 0 ? (
        <div className="ui-empty">
          Todas las ciudades de este departamento ya tienen costo propio.
        </div>
      ) : (
        <div className="ui-checklist">
          {cities.map((city) => {
            const checked = selectedIds.includes(city.id);
            return (
              <label
                key={city.id}
                className={`ui-checklist__item${checked ? " ui-checklist__item--on" : ""}`}
              >
                <input
                  type="checkbox"
                  className="visually-hidden"
                  checked={checked}
                  onChange={() => toggleCity(city.id)}
                />
                <span className="ui-checklist__box">
                  {checked && <i className="ri-check-line" />}
                </span>
                <span>{city.name}</span>
              </label>
            );
          })}
        </div>
      )}

      <label className="ui-label" style={{ margin: "14px 0 6px" }} htmlFor="cities-cost">
        Costo de envío
      </label>
      <NumericFormat
        id="cities-cost"
        className="ui-input w-100"
        prefix={`${currency || "$"} `}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={2}
        fixedDecimalScale
        allowNegative={false}
        value={shippingValue}
        onValueChange={(values) => setShippingValue(values.value)}
        placeholder="Ej: 12.000"
      />

      {/*
        El botón dice cuántas se van a guardar: con casillas múltiples es la
        única confirmación de que se marcó lo que se creía marcar.
      */}
      <button
        type="button"
        className="ui-btn ui-btn--primary ui-btn--block"
        style={{ marginTop: 16 }}
        disabled={!selectedIds.length}
        onClick={handleSave}
      >
        {selectedIds.length === 0
          ? "Selecciona ciudades"
          : `Guardar ${selectedIds.length} ${selectedIds.length === 1 ? "ciudad" : "ciudades"}`}
      </button>

    </div>
  );
});

export default AddCitiesPanel;
